<template>
  <div class="contents">
    <div class="container-fluid">
      <div class="row">
        <div class="col-lg-12">
          <div class="breadcrumb-main user-member justify-content-sm-between">
            <div class="d-flex flex-wrap justify-content-center breadcrumb-main__wrapper">
              <h4 class="text-capitalize fw-500 breadcrumb-title">
                <router-link to="/mayar-integration"><i class="fas fa-arrow-left"></i></router-link>
                &nbsp; Webhook Log — {{ integrationName }}
              </h4>
            </div>
            <div class="action-btn">
              <button class="btn btn-light" @click="loadData" :disabled="isLoading">
                <i class="fas fa-sync"></i> Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="alert alert-info">
        <b>Tips Debug:</b>
        Setiap kali Mayar trigger webhook, log akan muncul disini. Cek <b>raw payload</b> untuk lihat data yang Mayar kirim,
        gunakan untuk pasang <b>placeholder</b> di template Anda.
        Status <code>processed</code> = sukses kirim, <code>skipped</code> = event di-filter, <code>error</code> = ada masalah, <code>invalid_signature</code> = signature mismatch.
      </div>

      <div class="row">
        <div class="col-lg-12">
          <div class="userDatatable global-shadow border p-30 bg-white radius-xl w-100 mb-30">
            <div v-if="isLoading" class="text-center">
              <div class="spinner-border" role="status"></div>
            </div>
            <div v-else-if="logs.length === 0" class="text-center text-muted p-4">
              <p>Belum ada webhook masuk.</p>
            </div>
            <div v-else class="table-responsive">
              <table class="table table-hover">
                <thead class="bg-light">
                  <tr>
                    <th>Waktu</th>
                    <th>Event</th>
                    <th>Status</th>
                    <th class="text-center">Sent</th>
                    <th>Error</th>
                    <th class="text-right">Detail</th>
                  </tr>
                </thead>
                <tbody>
                  <template v-for="log in logs">
                    <tr :key="log.id">
                      <td><small>{{ formatTime(log.created_at) }}</small></td>
                      <td><code>{{ log.event || '-' }}</code></td>
                      <td>
                        <span :class="statusClass(log.status)">{{ log.status }}</span>
                      </td>
                      <td class="text-center">{{ log.sent_count }}</td>
                      <td><small class="text-danger">{{ truncate(log.error_message, 60) }}</small></td>
                      <td class="text-right">
                        <button class="btn btn-sm btn-light" @click="toggleExpand(log.id)" title="Detail">
                          <i :class="expanded[log.id] ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
                        </button>
                      </td>
                    </tr>
                    <tr v-if="expanded[log.id]" :key="'detail-' + log.id" class="bg-light">
                      <td colspan="6">
                        <div style="padding: 12px;">
                          <h6>Raw Payload:</h6>
                          <pre class="bg-dark text-white p-3" style="max-height: 300px; overflow:auto; font-size:11px;">{{ formatJSON(log.raw_payload) }}</pre>
                          <h6 class="mt-3">Headers:</h6>
                          <pre class="bg-secondary text-white p-2" style="max-height: 150px; overflow:auto; font-size:11px;">{{ formatJSON(log.request_headers) }}</pre>
                          <div v-if="log.error_message" class="mt-3 alert alert-danger">
                            <b>Error:</b> {{ log.error_message }}
                          </div>
                          <div v-if="log.response_text" class="mt-3 alert alert-secondary">
                            <b>Response Note:</b> {{ log.response_text }}
                          </div>
                        </div>
                      </td>
                    </tr>
                  </template>
                </tbody>
              </table>
            </div>

            <!-- Pagination -->
            <div v-if="total > limit" class="d-flex justify-content-between align-items-center mt-3">
              <small class="text-muted">{{ offset + 1 }}-{{ Math.min(offset + limit, total) }} dari {{ total }}</small>
              <div>
                <button class="btn btn-sm btn-light" :disabled="offset === 0" @click="prevPage">
                  <i class="fas fa-chevron-left"></i> Prev
                </button>
                <button class="btn btn-sm btn-light ml-2" :disabled="offset + limit >= total" @click="nextPage">
                  Next <i class="fas fa-chevron-right"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import moment from "moment";

export default {
  data() {
    return {
      integrationId: null,
      integrationName: "...",
      logs: [],
      total: 0,
      limit: 20,
      offset: 0,
      isLoading: true,
      expanded: {},
    };
  },
  mounted() {
    this.integrationId = parseInt(this.$route.params.id);
    this.loadIntegration();
    this.loadData();
  },
  methods: {
    async loadIntegration() {
      try {
        const res = await axios.get("mayar-integration/" + this.integrationId);
        this.integrationName = res.data.data.integration.name;
      } catch (err) {
        // silent
      }
    },
    async loadData() {
      this.isLoading = true;
      try {
        const res = await axios.get(
          "mayar-integration/" + this.integrationId + "/logs",
          { params: { limit: this.limit, offset: this.offset } }
        );
        this.logs = res.data.data.logs || [];
        this.total = res.data.data.total || 0;
      } catch (err) {
        this.$toast.error(err.response?.data?.message || "Gagal memuat log");
      } finally {
        this.isLoading = false;
      }
    },
    toggleExpand(id) {
      this.$set(this.expanded, id, !this.expanded[id]);
    },
    formatTime(ts) {
      if (!ts) return "-";
      return moment(parseInt(ts)).format("YYYY-MM-DD HH:mm:ss");
    },
    truncate(s, n) {
      if (!s) return "";
      return s.length > n ? s.slice(0, n) + "..." : s;
    },
    formatJSON(s) {
      if (!s) return "(empty)";
      try {
        return JSON.stringify(JSON.parse(s), null, 2);
      } catch (e) {
        return s;
      }
    },
    statusClass(status) {
      const map = {
        processed: "badge badge-success",
        skipped: "badge badge-secondary",
        error: "badge badge-danger",
        invalid_signature: "badge badge-warning",
        pending: "badge badge-info",
      };
      return map[status] || "badge badge-light";
    },
    prevPage() {
      this.offset = Math.max(0, this.offset - this.limit);
      this.loadData();
    },
    nextPage() {
      this.offset = this.offset + this.limit;
      this.loadData();
    },
  },
};
</script>
